/**
 * @fileoverview Pantalla 3 — Datos demográficos.
 * Captura alcaldía (obligatoria) + edad/género/grupo (opcionales) y
 * llama a iniciarParticipacion para obtener el session_token.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { useParticipacion } from "../context/ParticipacionContext";
import { iniciarParticipacion } from "../lib/api";
import { ALCALDIAS_ORDENADAS } from "../lib/alcaldias";
import { mensajeDeError } from "../lib/utils-app";

const EDAD_OPCIONES = [
    { valor: "18-29", etiqueta: "18 a 29 años" },
    { valor: "30-44", etiqueta: "30 a 44 años" },
    { valor: "45-59", etiqueta: "45 a 59 años" },
    { valor: "60+", etiqueta: "60 años o más" },
];

const GENERO_OPCIONES = [
    { valor: "mujer", etiqueta: "Mujer" },
    { valor: "hombre", etiqueta: "Hombre" },
    { valor: "otro", etiqueta: "Otro" },
];

const GRUPO_OPCIONES = [
    { valor: "ninguno", etiqueta: "Ninguno" },
    { valor: "persona_con_discapacidad", etiqueta: "Persona con discapacidad" },
    { valor: "persona_mayor", etiqueta: "Persona mayor (60 años o más)" },
    { valor: "comunidad_originaria", etiqueta: "Pueblo o barrio originario" },
];

/**
 * Botones tipo radio horizontal con scroll si no caben.
 * @param {{
 *   id: string,
 *   label: string,
 *   value: string | null,
 *   onChange: (v: string | null) => void,
 *   options: { valor: string, etiqueta: string }[],
 *   testid: string,
 *   permitirNoDecir?: boolean,
 * }} props
 */
function GrupoBotones({ id, label, value, onChange, options, testid, permitirNoDecir = true }) {
    const opciones = permitirNoDecir
        ? [...options, { valor: "__nodecir", etiqueta: "Prefiero no decir" }]
        : options;

    return (
        <div role="radiogroup" aria-labelledby={`${id}-label`} className="space-y-2">
            <p id={`${id}-label`} className="text-sm font-medium">
                {label}
            </p>
            <div className="flex flex-wrap gap-2" data-testid={testid}>
                {opciones.map((o) => {
                    const isSelected =
                        (o.valor === "__nodecir" && value === null) || value === o.valor;
                    return (
                        <button
                            key={o.valor}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            onClick={() =>
                                onChange(o.valor === "__nodecir" ? null : o.valor)
                            }
                            className={[
                                "px-4 py-2 rounded-full text-sm font-medium border transition-all",
                                isSelected
                                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                    : "bg-card border-border text-foreground hover:border-primary/50",
                            ].join(" ")}
                            data-testid={`${testid}-${o.valor}`}
                        >
                            {o.etiqueta}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function DatosDemograficos() {
    const navigate = useNavigate();
    const { state, dispatch } = useParticipacion();

    const [alcaldia, setAlcaldia] = useState(/** @type {string} */ (""));
    const [edad, setEdad] = useState(/** @type {string | null} */ (null));
    const [genero, setGenero] = useState(/** @type {string | null} */ (null));
    const [grupo, setGrupo] = useState(/** @type {string | null} */ (null));
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const [errorAlcaldia, setErrorAlcaldia] = useState(false);

    useEffect(() => {
        if (!state.codigo) {
            navigate("/", { replace: true });
        }
    }, [state.codigo, navigate]);

    async function handleEmpezar() {
        if (!alcaldia) {
            setErrorAlcaldia(true);
            return;
        }
        if (!state.codigo) return;
        setEnviando(true);
        setError(null);
        try {
            const datos = {
                alcaldia_residencia: alcaldia,
                edad_rango: /** @type {any} */ (edad),
                genero: /** @type {any} */ (genero),
                grupo: /** @type {any} */ (grupo),
            };
            const res = await iniciarParticipacion(state.codigo, datos);
            dispatch({ type: "SET_DATOS", payload: datos });
            dispatch({
                type: "SET_SESION",
                payload: {
                    session_token: res.session_token,
                    participacion_id: res.participacion_id,
                    expira_en: res.expira_en,
                },
            });
            navigate("/votacion");
        } catch (e) {
            // Si el código expiró entre la validación y el inicio (race),
            // volvemos a Inicio con el mensaje contextual del backend.
            const code = /** @type {any} */ (e)?.code;
            if (code === "token_expirado") {
                const msg = mensajeDeError(e);
                dispatch({ type: "RESET" });
                navigate("/", { replace: true, state: { errorInicial: msg } });
                return;
            }
            setError(mensajeDeError(e));
            setEnviando(false);
        }
    }

    return (
        <AppShell testid="page-datos">
            <div className="space-y-6">
                <div className="space-y-2">
                    <h1 className="font-serif-headline text-2xl sm:text-3xl font-semibold text-foreground">
                        Antes de empezar, cuéntanos un poco de ti
                    </h1>
                    <p className="text-base text-muted-foreground">
                        Esta información nos ayuda a entender mejor las respuestas. Solo la
                        alcaldía es obligatoria; lo demás puedes saltártelo.
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Alcaldía */}
                    <div className="space-y-2">
                        <Label htmlFor="alcaldia-select" className="text-sm font-medium">
                            Alcaldía donde vives <span className="text-destructive">*</span>
                        </Label>
                        <Select
                            value={alcaldia}
                            onValueChange={(v) => {
                                setAlcaldia(v || "");
                                setErrorAlcaldia(false);
                            }}
                        >
                            <SelectTrigger
                                id="alcaldia-select"
                                className="h-12 text-base"
                                aria-invalid={errorAlcaldia}
                                data-testid="alcaldia-select"
                            >
                                <SelectValue placeholder="Selecciona tu alcaldía" />
                            </SelectTrigger>
                            <SelectContent>
                                {ALCALDIAS_ORDENADAS.map((a) => (
                                    <SelectItem
                                        key={a.codigo}
                                        value={a.codigo}
                                        data-testid={`alcaldia-option-${a.codigo}`}
                                    >
                                        {a.nombre}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errorAlcaldia ? (
                            <p
                                role="alert"
                                className="text-xs text-destructive"
                                data-testid="alcaldia-error"
                            >
                                Por favor selecciona tu alcaldía.
                            </p>
                        ) : null}
                    </div>

                    <GrupoBotones
                        id="edad"
                        label="Tu rango de edad"
                        value={edad}
                        onChange={setEdad}
                        options={EDAD_OPCIONES}
                        testid="edad-grupo"
                    />

                    <GrupoBotones
                        id="genero"
                        label="Género"
                        value={genero}
                        onChange={setGenero}
                        options={GENERO_OPCIONES}
                        testid="genero-grupo"
                    />

                    <GrupoBotones
                        id="grupo"
                        label="¿Te identificas con alguno de estos grupos?"
                        value={grupo}
                        onChange={setGrupo}
                        options={GRUPO_OPCIONES}
                        testid="grupo-grupo"
                    />
                </div>

                {error ? (
                    <p
                        role="alert"
                        className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                        data-testid="datos-error"
                    >
                        {error}
                    </p>
                ) : null}

                <Button
                    size="lg"
                    onClick={handleEmpezar}
                    disabled={enviando}
                    className="w-full h-12 text-base font-semibold"
                    data-testid="empezar-btn"
                >
                    {enviando ? (
                        <>
                            <LoadingSpinner size={18} className="mr-2" /> Iniciando...
                        </>
                    ) : (
                        "Empezar"
                    )}
                </Button>
            </div>
        </AppShell>
    );
}
